import { useMemo } from 'react'

import {
  matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import type { FilterCompletionStatusKey } from '@functions/orderItemTask'
import { matchCompletionStatusKey } from '@functions/orderItemTask'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignQuoteStatusKeys, OrderItemTaskModel, OrderItemTaskRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['orderItemTasks', 'statuses']

export type OrderItemTaskFilters = {
  campaign_id?: number,
  completion_status_key?: FilterCompletionStatusKey,
  limit?: number,
  order_item_id?: number,
  order_item_task_type_id?: number,
  order_item_task_type_key?: string,
  provider_employee_id?: number,
  provider_id?: number,
  status_id?: number,
  status_key?: CampaignQuoteStatusKeys | 'all',
  string?: string,
}

type UseOrderItemTasksOptions = {
  filters?: OrderItemTaskFilters,
  performHttpRequests?: boolean,
  requestOptions?: OrderItemTaskRequestOptions,
  sortBy?: string,
}

function useOrderItemTasks(options: UseOrderItemTasksOptions) {
  const { filters = {}, requestOptions, sortBy = 'address asc' } = options
  const {
    campaign_id: filterCampaignId,
    completion_status_key: filterCompletionStatusKey,
    limit: filterLimit,
    order_item_id: filterOrderItemId,
    order_item_task_type_id: filterOrderItemTaskTypeId,
    order_item_task_type_key: filterOrderItemTaskTypeKey,
    provider_employee_id: filterProviderEmployeeId,
    provider_id: filterProviderId,
    status_key: filterStatusKey,
    string: filterString,
  } = filters

  const {
    updatedEntities: {
      orders: ordersUpdatedAt,
      orderItems: orderItemsUpdatedAt,
      orderItemTasks: orderItemTasksUpdatedAt,
      statuses: statusesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const {
    orders, orderItems, orderItemTasks, orderItemTaskTypes, statuses,
  } = useSelector(reduxState => reduxState.entities)

  const filteredOrderItemTasks = useMemo(() => {
    const filtered = Object.values(orderItemTasks).filter((orderItemTask: OrderItemTaskModel) => {
      const {
        address,
        order_item_id,
        order_item_task_type_id,
        provider_employee_id,
        provider_id,
        status_id,
      } = orderItemTask

      // @ts-expect-error TS2538
      const orderItem = orderItems[order_item_id] || {}
      const order = orders[orderItem.order_id] || {}

      const orderItemTaskType = orderItemTaskTypes[order_item_task_type_id] || {}
      const status = status_id && statuses[status_id] ? statuses[status_id] : {}

      // Match Campaign
      const matchCampaign = matchFilterNumber(order.campaign_id, filterCampaignId)
        || matchFilterNumber(orderItem.campaign_id, filterCampaignId)

      // Match Order Item Task Type by Key
      const matchOrderItemTaskTypeKey = filterOrderItemTaskTypeKey === 'all'
        ? true
        : matchFilterString(orderItemTaskType?.short_name, filterOrderItemTaskTypeKey, { exact: true })

      // Match Status
      const matchStatusKey = filterStatusKey === 'all'
        ? true
        // @ts-expect-error TS2339
        : matchFilterString(status?.key, filterStatusKey, { exact: true })

      const matchCompletionStatus = matchCompletionStatusKey({
        status_id, filterCompletionStatusKey, orderItem, statuses,
      })

      const matchOrderItemId = matchFilterNumber(order_item_id, filterOrderItemId)
      const matchOrderItemTaskTypeId = matchFilterNumber(order_item_task_type_id, filterOrderItemTaskTypeId)
      const matchProviderEmployeeId = matchFilterNumber(provider_employee_id, filterProviderEmployeeId)
      const matchProviderId = matchFilterNumber(provider_id, filterProviderId)

      const matchAddress = matchFilterString(address, filterString)
      const matchOptionString = matchFilterString(orderItem.options_string, filterString)
      const matchOrderItemTitle = matchFilterString(orderItem.title, filterString)
      const matchString = matchAddress || matchOptionString || matchOrderItemTitle

      return matchCampaign
        && matchCompletionStatus
        && matchOrderItemId
        && matchOrderItemTaskTypeId
        && matchOrderItemTaskTypeKey
        && matchProviderEmployeeId
        && matchProviderId
        && matchStatusKey
        && matchString
    })

    const [column, direction] = sortBy.split(' ')
    // @ts-expect-error TS2345
    return sortArrayBy(filtered, direction, column)
  }, [ordersUpdatedAt, orderItemsUpdatedAt, orderItemTasksUpdatedAt, statusesUpdatedAt, JSON.stringify(options)])

  const filteredOrderItemTasksCount = filteredOrderItemTasks.length
  const hasFilteredOrderItemTasks = !!filteredOrderItemTasksCount

  const loadMorePayload = useLoadMore({
    filters,
    limit: filterLimit,
    loadedCount: filteredOrderItemTasksCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingOrderItemTasks } = useReduxAction(
    'orderItemTasks',
    'loadOrderItemTasks',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredOrderItemTasks,
    filteredOrderItemTasksCount,
    hasFilteredOrderItemTasks,
    loading: loadingOrderItemTasks,
  }
}

export default useOrderItemTasks
